// Cvanto Search Phase 1.5 — Unified Search's Agent Mode (handoff §19/§20).
// Opened exclusively via SearchHero's orb, same entry point and same
// "sheet over the Search screen" shell CvantoAISearchModal.js used — but
// now backed by the real Phase 1 conversational core (POST /api/cvanto-ai/*,
// SSE streaming, multi-turn, cards-before-text) instead of the old one-shot
// /api/search-assistant parse->interpret->confirm flow.
//
// Deliberately reuses the CvantoAI chat components AS-IS (useCvantoChat,
// MessageBubble, Composer, ConversationList — handoff §22.3: "decompose and
// re-host, not throw away"), not a rebuild: this modal is only the shell
// (backdrop/sheet/close/history-back handling, carried over from
// CvantoAISearchModal.js) plus a conversation-history drawer around them.
// MessageBubble/Composer already navigate (listing/profile/"show all
// results"/request links) via react-router directly — a "show all results"
// tap lands on /search?q=..., which ordinary Search's own /api/search/feed
// then serves, using the SAME shared relevance signal (server/search/
// domainTier.js) the AI path itself now uses. No result-handoff plumbing
// needed here, unlike the old modal's onShowResults/onCreateRequest props.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import CvantoAIVisual from "./CvantoAIVisual";
import ConversationList from "../CvantoAI/ConversationList";
import MessageBubble, { AiMark } from "../CvantoAI/MessageBubble";
import Composer from "../CvantoAI/Composer";
import useCvantoChat from "../CvantoAI/useCvantoChat";
import "../CvantoAI/CvantoAI.scss";
import "./CvantoAgentModal.scss";

const SUGGESTIONS = [
  "Чорна куртка до 4000 грн",
  "Кросівки Nike 43 розмір",
  "Манікюр у Львові",
  "iPhone 13 до 20 000 грн",
];

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 7h14M5 12h14M5 17h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ComposeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20l4.2-1 10-10a2 2 0 0 0-2.8-2.8l-10 10L4 20zM14.5 7.5l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CLOSE_ANIM_MS = 280;

// entryMode: 'voice' | 'text' — decided by which orb-menu button opened it.
//
// Phase 1.5 §25/§26 — this is Unified Search's Agent surface, not a
// standalone chatbot: <1024px it is a bottom sheet over SearchResults
// (unchanged from the original modal shell); >=1024px it becomes a docked
// side panel (CSS only, see CvantoAgentModal.scss) so the catalogue stays
// visible and scrollable beside the conversation, per §25.12/§26.4.
//
// `searchContext` — the host's CURRENT manual search state (query/city/
// price/...), used only to seed a brand-new conversation (handoff §25.7
// point 1: opening the agent from an existing search continues it).
// `onResults` — called with each turn's structured results payload so the
// host can rebuild the MAIN catalogue from it (§25.5: "conversation
// controls search; SearchResults displays marketplace inventory" — never a
// second, chat-bubble-only result list). MessageBubble is rendered with
// `hideResults` here for exactly that reason.
const CvantoAgentModal = ({ open, entryMode, onClose, searchContext, onResults }) => {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeTimerRef = useRef(null);
  const pushedHistoryRef = useRef(false);
  const closingViaPopRef = useRef(false);
  // Set right before a handoff navigation MessageBubble itself triggers
  // (listing/profile tap, "Показати всі результати", a request link) — same
  // reasoning as CvantoAISearchModal.js's own ref: a plain history.back()
  // would otherwise pop the WRONG entry once react-router has already
  // pushed its own on top of this modal's history marker.
  const handoffNavigatedRef = useRef(false);

  const chat = useCvantoChat({ seedSearch: searchContext });
  const { conversations, currentId, messages, sending, usage, send, sendAction, retry, startNewConversation, openConversation } = chat;

  // Forward each turn's results to the host the moment they land — before
  // waiting for the (slower) composer text — so the main catalogue starts
  // rebuilding as soon as results stream in, not on `done`.
  const lastForwardedResultsRef = useRef(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && last.results && last.results !== lastForwardedResultsRef.current) {
      lastForwardedResultsRef.current = last.results;
      onResults?.(last.results);
    }
  }, [messages, onResults]);

  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastMessage = messages[messages.length - 1];

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastMessage?.text, lastMessage?.results, lastMessage?.phase]);

  // Mount/active two-step (same pattern as CvantoAISearchModal.js) so the
  // exit transition plays before the component unmounts.
  useEffect(() => {
    if (open) {
      clearTimeout(closeTimerRef.current);
      setMounted(true);
      setDrawerOpen(false);
      // Every open starts a genuinely fresh conversation — same "clean
      // session per open" behavior the old modal had (fullCleanup on
      // open/close). The history drawer is how a user reaches a PAST
      // conversation instead, not a reopen.
      startNewConversation();
      stickToBottomRef.current = true;

      const raf = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(raf);
    }

    if (!open && mounted) {
      setActive(false);
      closeTimerRef.current = setTimeout(() => setMounted(false), CLOSE_ANIM_MS);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // Own scroll surface — the underlying Search screen must not scroll behind
  // a full-screen mobile/tablet sheet. The desktop (>=1024px) layout is a
  // DOCKED side panel instead (CvantoAgentModal.scss) — the whole point is
  // that the catalogue behind it stays visible AND scrollable (handoff
  // §25.4/§25.12), so body scroll is deliberately left alone there.
  useEffect(() => {
    if (!mounted) return undefined;
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mq = window.matchMedia("(min-width: 1024px)");
    const prevOverflow = document.body.style.overflow;

    const apply = () => {
      document.body.style.overflow = mq.matches ? prevOverflow : "hidden";
    };
    apply();
    mq.addEventListener("change", apply);

    return () => {
      mq.removeEventListener("change", apply);
      document.body.style.overflow = prevOverflow;
    };
  }, [mounted]);

  // Lightweight History API entry so a mobile hardware/browser back gesture
  // closes the modal instead of navigating away from /search.
  useEffect(() => {
    if (!open) return undefined;

    window.history.pushState({ cvantoAgentModal: true }, "");
    pushedHistoryRef.current = true;

    const onPopState = () => {
      closingViaPopRef.current = true;
      pushedHistoryRef.current = false;
      onClose?.();
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const requestClose = useCallback(() => {
    if (handoffNavigatedRef.current) {
      handoffNavigatedRef.current = false;
      pushedHistoryRef.current = false;
      onClose?.();
      return;
    }
    if (pushedHistoryRef.current && !closingViaPopRef.current) {
      pushedHistoryRef.current = false;
      window.history.back();
      return;
    }
    closingViaPopRef.current = false;
    onClose?.();
  }, [onClose]);

  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") requestCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // A tap inside MessageBubble (listing/profile/request/"show all results")
  // navigates the whole app away from /search — the route change unmounts
  // this modal along with it, so there's nothing further to unwind here;
  // just mark it so requestClose (if it still fires from cleanup) doesn't
  // also fight the router with a stray history.back().
  useEffect(() => {
    if (!mounted) return undefined;
    const onClick = (e) => {
      if (e.target.closest("a, button")?.closest(".cvai-results, .cvai-actions, .cvai-msg")) {
        handoffNavigatedRef.current = true;
      }
    };
    const sheet = scrollRef.current;
    sheet?.addEventListener("click", onClick, true);
    return () => sheet?.removeEventListener("click", onClick, true);
  }, [mounted]);

  if (!mounted) return null;

  const handleSend = (text) => {
    stickToBottomRef.current = true;
    return send(text);
  };

  const handleBackdropClick = () => requestClose();

  const openFromDrawer = (id) => {
    openConversation(id);
    setDrawerOpen(false);
  };

  const newChatFromDrawer = () => {
    startNewConversation();
    setDrawerOpen(false);
  };

  const remaining = usage?.remaining;
  const empty = messages.length === 0;
  const title = conversations.find((c) => c.id === currentId)?.title || "Cvanto AI Search";
  const orbState = sending ? "searching" : empty ? "idle" : "results";

  return (
    <div className={`cvanto-agent-modal ${active ? "is-active" : ""}`} role="dialog" aria-modal="true" aria-label="Cvanto AI Search">
      <button type="button" className="cvanto-agent-modal__backdrop" onClick={handleBackdropClick} aria-label="Закрити" tabIndex={-1} />

      <div className="cvanto-agent-modal__sheet">
        <header className="cvanto-agent-modal__header">
          <button type="button" className="cvanto-agent-modal__icon-btn" onClick={() => setDrawerOpen(true)} aria-label="Розмови">
            <ListIcon />
          </button>
          <AiMark />
          <h2 className="cvanto-agent-modal__title">{title}</h2>
          <button type="button" className="cvanto-agent-modal__icon-btn" onClick={newChatFromDrawer} aria-label="Нова розмова">
            <ComposeIcon />
          </button>
          <button type="button" className="cvanto-agent-modal__icon-btn cvanto-agent-modal__close" onClick={requestClose} aria-label="Закрити Cvanto AI Search">
            <CloseIcon />
          </button>
        </header>

        {drawerOpen && (
          <div className="cvanto-agent-modal__drawer">
            <button type="button" className="cvanto-agent-modal__drawer-scrim" aria-label="Закрити список розмов" onClick={() => setDrawerOpen(false)} />
            <div className="cvanto-agent-modal__drawer-panel">
              <ConversationList
                conversations={chat.conversations}
                loading={chat.conversationsLoading}
                error={chat.conversationsError}
                hasMore={chat.hasMoreConversations}
                activeId={currentId}
                onOpen={openFromDrawer}
                onNew={newChatFromDrawer}
                onDelete={async (id) => {
                  await chat.removeConversation(id);
                }}
                onDeleteAll={chat.removeAllConversations}
                onRename={chat.rename}
                onRetry={chat.loadConversations}
                onLoadMore={chat.loadMoreConversations}
              />
            </div>
          </div>
        )}

        <div className="cvai__scroll cvanto-agent-modal__scroll" ref={scrollRef} onScroll={onScroll} aria-live="polite" aria-busy={sending}>
          {chat.hasMoreHistory && (
            <button type="button" className="cvai-link-btn cvai__older" onClick={chat.loadOlder} disabled={chat.historyLoading}>
              Показати попередні повідомлення
            </button>
          )}

          {empty && (
            <div className="cvai__empty">
              <CvantoAIVisual state={orbState} decorative />
              <h2 className="cvai__empty-title">Що шукаємо?</h2>
              <p className="cvai__empty-sub">
                Опишіть, що вам потрібно — товар, послугу чи магазин. Я знайду це серед реальних оголошень Cvanto.
              </p>
              <div className="cvai__chips">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="cvai__chip" disabled={sending} onClick={() => handleSend(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              isLast={i === messages.length - 1}
              sending={sending}
              onAction={sendAction}
              onRetry={retry}
              hideResults
            />
          ))}
        </div>

        <Composer
          onSend={handleSend}
          disabled={sending}
          remaining={remaining}
          autoFocus={entryMode !== "voice"}
          autoStartVoice={entryMode === "voice"}
        />
      </div>
    </div>
  );
};

export default CvantoAgentModal;
